import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
class FlatFeeMilestone extends Base implements EntityType {
    flatFeeMilestoneId: uuid;
    name: string;
    amount: number;
    description?: string | null;
    completionDate?: Date | null;
    earned: boolean;
    invoiceId?: uuid | null;
    percentage?: number | null;
    earnedThisPeriod?: number | null;
    earnedAfterThisPeriod: boolean;
    position?: number | null;
    constructor(doc: typeof FlatFeeMilestone | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            flatFeeMilestoneId: genUUID(),
            name: null,
            amount: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "flatFeeMilestone";
        // @ts-ignore
        this.flatFeeMilestoneId = doc._flatFeeMilestoneId || doc.flatFeeMilestoneId || doc.flatfeemilestoneid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.amount = util.coalesce(util.parseFloat(doc._amount), util.parseFloat(doc.amount), util.parseFloat(doc.amount), null);
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.completionDate = DateUtil.timestampOnly(doc._completionDate) || DateUtil.timestampOnly(doc.completionDate) || DateUtil.timestampOnly(doc.completiondate) || null;
        // @ts-ignore
        this.earned = util.defaultBoolean([doc.earned, doc._earned, doc.earned, false]);
        // @ts-ignore
        this.invoiceId = doc._invoiceId || doc.invoiceId || doc.invoiceid || null;
        // @ts-ignore
        this.percentage = util.coalesce(util.parseFloat(doc._percentage), util.parseFloat(doc.percentage), util.parseFloat(doc.percentage), null);
        // @ts-ignore
        this.earnedThisPeriod = util.coalesce(util.parseFloat(doc._earnedThisPeriod), util.parseFloat(doc.earnedThisPeriod), util.parseFloat(doc.earnedthisperiod), null);
        // @ts-ignore
        this.earnedAfterThisPeriod = util.defaultBoolean([doc.earnedAfterThisPeriod, doc._earnedAfterThisPeriod, doc.earnedafterthisperiod, false]);
        // @ts-ignore
        this.position = util.coalesce(util.parseInt(doc._position), util.parseInt(doc.position), util.parseInt(doc.position), null);
    }
    get id(): uuid { return this.flatFeeMilestoneId; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this flat fee milestone"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","comment":"Associated firm"}]; }
    toLowerCase() { return this.flatFeeMilestoneId; }
    static override getNonRefArrayFields(): string[] { return []; }
    static override getDefaultEntries(): FlatFeeMilestone[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'flatFeeMilestone',
    dataArea: 'Billing',
    attachedTo: {parents: ['flatFee'], via: 'entity', onDelete: 'delete', onReplace: 'move', blockWhen: {nonEmpty: 'invoiceId'}},
    table: 'FLAT_FEE_MILESTONE',
    comment: 'A milestone representing a billable portion of a flat fee. Flat fees can be split into milestones, each with its own name, amount, and service description; when a milestone is marked earned and given a completion date it can be billed on its own invoice independently of the remaining flat fee balance.',
    fields: {
        flatFeeMilestoneId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this flat fee milestone'
        },

        /**
         * The name of this flat fee milestone
         */
        name: {type: 'string', required: true, comment: 'The name of the flat fee milestone'},

        /**
         * The amount of money allocated to the completion of this milestone
         */
        amount: {type: 'money', required: true, comment: 'The amount of money allocated to this bucket'},

        /**
         * A description of the service that must be provided before this milestone can be marked earned
         */
        description: {type: 'string', comment: 'A description of the service provided by this bucket'},

        /**
         * When was this milestone completed
         */
        completionDate: {type: 'timestamp', comment: 'Date and time when the milestone was completed'},

        /**
         * Flat fee buckets are often viewed as milestones, and the attorney can then charge the client
         * for that bucket.
         */
        earned: {type: 'boolean', comment: 'Do we consider this milestone as earned?'},

        /**
         * Which invoice this milestone is billed in.
         */
        invoiceId: {type: 'uuid', entityType: 'invoice', comment: 'The invoice this milestone is billed in'},

        /**
         * The UI needs to temporarily store the percentage of the parent fee
         */
        percentage: {type: 'float', transient: true, comment: 'Percentage of the parent fee'},

        /**
         * Was this milestone earned in this invoice period?
         */
        earnedThisPeriod: {type: 'money', transient: true, comment: 'Amount earned in the current invoice period?'},

        /**
         * Used in intermediate calculations
         */
        earnedAfterThisPeriod: {type: 'boolean', transient: true, comment: 'Was this milestone earned after the current period?'},

        /**
         * Positions for milestones
         */
        position: {
            type: 'int',
            comment: 'A position (order) for the flatFee (within a flatFee)'
        }
    },
    indexes: [
        {name: 'notDeleted', fn: t => t.status !== 'D'}
    ],
    maps: [
        {name: 'flatFeeId', keysFn: t => t.entityId, presentFn: (t, k) => t.entityId === k},
        {name: 'matterId', keysFn: t => t.entityType === 'matter' ? t.entityId : null, presentFn: (t, k) => t.entityId === k},
        {name: 'invoiceId', keysFn: t => t.invoiceId, presentFn: (t, k) => t.invoiceId === k},
    ]
});
    }
}
export default FlatFeeMilestone;
